import API from '@dotcom/api-middleware';
import {
  assetStorageResponse,
  getImagesResponse,
  linkImageResponse,
  uploadImageResponse,
  cleanUpImageResponse,
  cropAndRotateResponse,
  deletePhotoTrayImageResponse,
  successNoContentResponse,
} from '../data';
import {
  ImageResponse,
  LinkPhotoResponse,
  CleanUpImageResponse,
  CropAndRotateResponse,
  linkPhotoFormData,
  cleanUpFormData,
  cropAndRotateFormData,
} from '../global-types';
import { AssetStorageResponse, LinkedAsset, LinkedAssets } from '../global-types/images';
import { ApiResponse, ErrorResponse, SuccessNoContentResponse } from '../global-types/services';
import { getDateFromDaysAgo } from '../utils/get-date-from-days-ago';
import { getAccountIdFromCookie } from '../utils/utility/get-account-id-from-cookie';
import { processResponse } from './middleware';
import { S3Uploader } from './s3-upload';

const PODService = new API();

const mockResponse = process.env.REACT_APP_MOCK_INITIALIZATION_SERVICE === 'true';

export const linkPhoto = (formData: linkPhotoFormData, project_id: string): Promise<ApiResponse<LinkPhotoResponse>> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const response: ApiResponse<LinkPhotoResponse> = linkImageResponse;
      resolve(response);
    }

    const response = PODService.post('/customization', `/${getAccountIdFromCookie()}/${project_id}/images`, formData);
    processResponse(response, resolve, reject);
  });
};

export const linkAssets = (assets: LinkedAsset[], project_id: string): Promise<LinkedAssets> => {
  return new Promise((resolve, reject) => {
    const response = PODService.post('/customization', `/${getAccountIdFromCookie()}/${project_id}/assets/link`, {
      assets,
    });
    processResponse(response, resolve, reject);
  });
};

export const uploadPreviewToS3 = async (
  formData: FormData,
  s3Url: string,
  uploadFile: S3Uploader,
): Promise<ApiResponse<unknown> | string> => {
  const file = formData.get('file') as File;
  const [imageUrl] = s3Url.split('?');
  await uploadFile(s3Url, file);
  return imageUrl;
};

export const uploadPhotoS3 = async (
  formData: FormData,
  isHandwritingImage: boolean,
  displayIndicator: boolean,
  s3Url: string,
  uploadFile: S3Uploader,
): Promise<ApiResponse<ImageResponse> | unknown> => {
  const file = formData.get('file') as File;

  return uploadFile(s3Url, file)
    .then(() => {
      return new Promise((resolve, reject) => {
        if (mockResponse) {
          const response: ApiResponse<ImageResponse> = uploadImageResponse;
          resolve(response);
        }

        const [image_url] = s3Url.split('?');
        const requestBody = {
          photo_tray_images: [
            {
              image_url: image_url,
              display_indicator: displayIndicator,
              is_handwriting_image: isHandwritingImage,
            },
          ],
        };

        const response = PODService.post(
          '/customization',
          `/${getAccountIdFromCookie()}/photo-tray-images/images`,
          requestBody,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          },
        );

        processResponse(response, resolve, reject);
      });
    })
    .catch((error) => {
      // eslint-disable-next-line no-console
      console.error(error);
      return Promise.reject(error);
    });
};

export const getAssetUrls = (count, format): Promise<ApiResponse<AssetStorageResponse>> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const response: ApiResponse<AssetStorageResponse> = assetStorageResponse;
      resolve(response);
    }

    const response = PODService.post('/customization', `/${getAccountIdFromCookie()}/asset-storage-url`, {
      count: count,
      format,
      location: 'images',
    });
    processResponse(response, resolve, reject);
  });
};

export const uploadToAssetStorage = (
  assetUrl: string,
  file: File,
  uploadFile: S3Uploader,
): Promise<SuccessNoContentResponse<unknown> | ErrorResponse | Awaited<ReturnType<S3Uploader>>> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const response: SuccessNoContentResponse<unknown> = assetStorageResponse;
      resolve(response);
    }

    uploadFile(assetUrl, file).then(
      (response) => {
        resolve(response);
      },
      (err) => {
        reject(err);
      },
    );
  });
};

export const getPhotos = (): Promise<ApiResponse<ImageResponse[]>> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const response: ApiResponse<ImageResponse[]> = getImagesResponse;
      resolve(response);
    }

    const response = PODService.get('/customization', `/${getAccountIdFromCookie()}/photo-tray-images`);
    processResponse(response, resolve, reject);
  });
};

export const getPhotosPagination = (page = 1): Promise<ApiResponse<ImageResponse[]>> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const response: ApiResponse<ImageResponse[]> = getImagesResponse;
      resolve(response);
    }
    const ninetyDaysAgo = getDateFromDaysAgo(90);
    const queryParams = `p[page]=[{"eq": "${page}"}]&p[limit]=[{"eq": "15"}]&q[created_at]=[{"gte":"${ninetyDaysAgo}"}]&q[sort_by]=[{"field":"created_at","order":"asc"}]`;
    const url = `/customization/${getAccountIdFromCookie()}/photo-tray-images?${queryParams}`;
    const response = PODService.get(url);
    processResponse(response, resolve, reject);
  });
};

export const cleanUpImage = (
  projectId: string,
  imageId: string,
  formData: cleanUpFormData,
): Promise<ApiResponse<CleanUpImageResponse>> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const response: ApiResponse<CleanUpImageResponse> = cleanUpImageResponse;
      resolve(response);
    }

    const response = PODService.post(
      '/customization',
      `/${getAccountIdFromCookie()}/${projectId}/images/${imageId}/cleanup`,
      formData,
    );
    processResponse(response, resolve, reject);
  });
};

export const deleteImage = (projectId: string, imageId: string): Promise<SuccessNoContentResponse<null>> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(successNoContentResponse);
    }

    const response = PODService.delete('/customization', `/${getAccountIdFromCookie()}/${projectId}/images/${imageId}`);
    processResponse(response, resolve, reject);
  });
};

export const deletePhotoTrayImage = (photoTrayImageId: string) => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(deletePhotoTrayImageResponse);
    }
    const response = PODService.delete(
      '/customization',
      `/${getAccountIdFromCookie()}/photo-tray-images/${photoTrayImageId}`,
    );
    processResponse(response, resolve, reject);
  });
};

export const deletePhotoTrayImages = (photoTrayImageIds: string[]) => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(successNoContentResponse);
    }

    const response = PODService.delete(
      '/customization',
      `/${getAccountIdFromCookie()}?photo_tray_image_ids=${photoTrayImageIds}`,
    );
    processResponse(response, resolve, reject);
  });
};

export const cropAndRotateImage = (
  projectId: string,
  imageId: string,
  formData: cropAndRotateFormData,
): Promise<ApiResponse<CropAndRotateResponse>> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      const response: ApiResponse<CropAndRotateResponse> = cropAndRotateResponse;
      resolve(response);
    }

    const response = PODService.post(
      '/customization',
      `/${getAccountIdFromCookie()}/${projectId}/images/${imageId}/crop-and-rotate`,
      formData,
    );

    processResponse(response, resolve, reject);
  });
};
